import * as Sentry from '@sentry/browser'
import { SUBMISSIONFORMDEFAULTOPTIONS, SUBMISSIONFORMFIELDTYPES, SUBMISSIONFORMPRESETOPTIONS } from '@content/config'
import { getKey, getSignedUrl } from './get'
import { capitalise } from './helpers'
import UploadDirector from '@app/components/Upload/UploadDirector'
import SignedUploadUrlFailureException from './exception/SignedUploadUrlFailureException'
import SubmissionUploadException from './exception/SubmissionUploadException'

/**
 * Build each form step based on separators in the consumerFields
 *
 * @param {Array} fields
 * The consumerFields array
 */
const buildSteps = (fields: any, component: any) => {
  let i = 0

  const result: any = fields.reduce((acc: any, formField: any) => {
    const exists = acc[i]
    const stepConfig: any = {
      id: `step-${i}`,
      component: component,
      props: {},
      fieldIds: [],
      previousId: i - 1 >= 0 ? `step-${i - 1}` : null,
      nextId: `step-${i + 1}`,
    }

    if (formField.id === 'separator') {
      i++
    } else if (!exists) {
      acc[i] = stepConfig
      acc[i].fieldIds = [...acc[i].fieldIds, formField.id]
    } else {
      acc[i].fieldIds = [...acc[i].fieldIds, formField.id]
    }

    return acc
  }, [])

  if (result.length > 0) {
    result[0].entryPoint = true
    result[result.length - 1].submitPoint = true
  }

  return result
}

/**
 * Shape the consumerField data so it is wizard friendly
 *
 * @param {Array} config
 * The consumerFields array
 */
const buildFields = (config: any, component: any) => {
  let hasPurchaseInformation = false
  let hasPurchaseProducts = false

  let fields: any = config.fields.reduce((acc: any, x: Submission.FieldConfig) => {
    const exists = acc.find((y: any) => y.id === x.field)
    const hasPresetOptions = SUBMISSIONFORMPRESETOPTIONS[x.field]
    const hasDefaultOptions = SUBMISSIONFORMDEFAULTOPTIONS[x.field]
    const hasConfigOptions = x.values
    const uploadDirectorConfig: Upload.DirectorConfig = {
      constraints: {
        maxFileSize: 15000000,
        maxFiles: 1,
        allowedMimeTypes: ['image/jpeg', 'image/gif', 'image/png', 'application/pdf'],
      },
      compression: {
        enabled: true,
        quality: 0.8,
        maxWidth: 2000,
        maxHeight: 2000,
      },
    }

    if (exists) return acc

    const baseConfig: any = {
      id: x.field,
      component: component,
      props: {
        label: x.displayName,
        tooltip: x.hint &&
          x.hint.trim().length > 0 && {
            text: x.hint.trim(),
            trigger: 'hover',
          },
        type: getKey(SUBMISSIONFORMFIELDTYPES, x.field),
      },
      required: x.required,
    }

    if (hasPresetOptions) {
      baseConfig.props.options = hasPresetOptions
    } else if (hasDefaultOptions && hasConfigOptions) {
      const mappedOptions = x.values
        .replace(/(,$)/, '')
        .split(',')
        .map((y: any) => ({ value: y.trim(), label: capitalise(y.trim()) }))
      baseConfig.props.options = [...mappedOptions, ...hasDefaultOptions]
    } else if (hasConfigOptions) {
      const isValidatable = x?.validatable

      baseConfig.props.options = x.values
        .replace(/(,$)/, '')
        .split(',')
        .map((y: any) => ({ value: isValidatable ? y.trim().toLowerCase() : y.trim(), label: capitalise(y.trim()) }))
    }

    if (x.field === config.universe.lookupField) {
      baseConfig.props.preReqField = config.universe.qualifyingField
      baseConfig.props.options = x.values
        .replace(/(,$)/, '')
        .split(',')
        .map((fieldValue: any) => {
          const prize = config.prizes.find((z: any) => z.lookupValue === fieldValue.toLowerCase())
          const value = fieldValue.trim()

          return {
            value,
            label: capitalise(value),
            prereq: config.universe.qualifyingField &&
              prize?.qualifyingValue && {
                fieldId: config.universe.qualifyingField,
                value: new RegExp(
                  `(${prize.qualifyingValue
                    ?.trim()
                    .replace(/(\(|\))/g, '\\$1')
                    .replace(/((\n)|(,$))/g, '')
                    .replace(/(,)/g, '|')})`
                ),
              },
          }
        })
    }

    if (baseConfig.props.type === 'currency') {
      baseConfig.props.currency = config.currency
      baseConfig.props.validationMsg = 'Please enter a valid amount e.g. 1.50 for £1.50'
      baseConfig.validation = (_fields: any, value: any) => {
        if (value.amount) {
          return true
        }
        return false
      }
    }

    if (baseConfig.props.type === 'bankDetails') {
      baseConfig.props.country = config.country
      baseConfig.required = true
      baseConfig.validation = (_fields: any, value: any) => {
        return value.valid
      }
    }

    if (baseConfig.props.type === 'upload' || baseConfig.props.type === 'purchaseInformation') {
      baseConfig.props.upload = new UploadDirector({
        ...uploadDirectorConfig,
        id: baseConfig.id,
      })
    }

    if (baseConfig.props.type === 'purchaseInformation') {
      hasPurchaseInformation = true

      baseConfig.validation = (_fields: any, value: any) => {
        if (value) {
          if (!Array.isArray(value)) return false
          if (value.length === 0) return false

          for (let i = 0; i < value.length; i++) {
            if (value[i].valid == false) return false
          }

          return true
        }

        return false
      }
    }

    if (baseConfig.props.type === 'purchaseProducts') {
      hasPurchaseProducts = true
    }

    // Add any custom props to component that are passed from config
    if (config?.props && config.props[x.field]) {
      baseConfig.props = { ...baseConfig.props, ...config.props[x.field] }
    }

    return [...acc, baseConfig]
  }, [])

  if (hasPurchaseInformation) {
    fields = pullNestedFieldsFromBuiltFields(fields, 'purchaseInformation')
  } else if (hasPurchaseProducts) {
    fields = pullNestedFieldsFromBuiltFields(fields, 'purchaseProducts')
  }

  // Check for special fields such as purchaseInformation and purchaseProducts
  // If they are present we then need to look for fields they will contain productId, invoiceNo, proofOfPurchase, proofOfPackaging
  return fields
}

const pullNestedFieldsFromBuiltFields = (fields: any, nameOfFieldToUpdate: string) => {
  const purchaseItemFields = ['productId', 'purchasePrice']
  const purchaseInformationFields = ['invoiceNo', 'proofOfPurchase', 'proofOfPackaging', ...purchaseItemFields]

  const fieldToUpdate = fields.findIndex((field: any) => field.props.type === nameOfFieldToUpdate)
  const searchFields = nameOfFieldToUpdate === 'purchaseInformation' ? purchaseInformationFields : purchaseItemFields

  const fieldsToAppend = fields.filter((field: any) => {
    return searchFields.includes(field.id)
  })

  fields[fieldToUpdate].props.formFields = fieldsToAppend

  return fields.filter((field: any) => {
    return !searchFields.includes(field.id)
  })
}

/**
 * Upload a single item
 *
 * @param {Object} res
 * The signed response from the server
 *
 * @param {Object} value
 * The current field value (a file object)
 */
const uploadItem = async (res: any, value: any) =>
  await fetch(res.uploadURL, {
    body: value,
    method: 'PUT',
    headers: { 'Content-Type': value.type },
  })

/**
 * Handle multiple file uploads
 *
 * @param {Object} config
 * The global config for this campaign
 *
 * @param {Object} fields
 * The submitted field data
 */
const handleUploads = async (fields: any, submissionUri: string, campaignId: string) => {
  const uploadFields: { [key: string]: Upload.DirectorFile } = Object.keys(fields).reduce((acc: any, field: any) => {
    if (getKey(SUBMISSIONFORMFIELDTYPES, field) === 'upload' && fields[field] !== null) {
      return { ...acc, [field]: fields[field] }
    }

    if (getKey(SUBMISSIONFORMFIELDTYPES, field) === 'purchaseInformation') {
      let purchaseInformationUploads = {}

      fields[field].forEach((purchaseItem: any) => {
        Object.keys(purchaseItem.fields).forEach((field: string) => {
          if (getKey(SUBMISSIONFORMFIELDTYPES, field) === 'upload' && purchaseItem.fields[field].value !== null) {
            purchaseInformationUploads = {
              ...purchaseInformationUploads,
              [purchaseItem.id + '-' + field]: purchaseItem.fields[field].value,
            }
          }
        })
      })

      return { ...acc, ...purchaseInformationUploads }
    }

    return acc
  }, {})

  const signedUploads = await Promise.all(
    Object.keys(uploadFields).map(async (field: string) => {
      try {
        if (uploadFields[field].uploadFilename) {
          return {
            field,
            file: uploadFields[field],
          }
        }

        const signature = await getSignedUrl(
          submissionUri,
          campaignId,
          field,
          uploadFields[field].name,
          uploadFields[field].type
        )
        return {
          signature,
          field,
          file: uploadFields[field],
        }
      } catch (error) {
        throw new SignedUploadUrlFailureException(field, uploadFields[field], error)
      }
    })
  )

  const uploaded = await Promise.all(
    signedUploads.map(async (signedUpload) => {
      if (signedUpload.file.uploadFilename) {
        return {
          field: signedUpload.field,
          filename: signedUpload.file.uploadFilename,
          status: 200,
        }
      }
      try {
        const uploadResponse = await uploadItem(signedUpload.signature, signedUpload.file.data)
        const upload = {
          field: signedUpload.field,
          filename: signedUpload.signature.uploadFilename,
          status: uploadResponse.status,
        }

        if (uploadResponse.status !== 200) {
          return Promise.reject(new SubmissionUploadException(uploadResponse, signedUpload.field, signedUpload.file))
        }

        uploadFields[signedUpload.field].uploadFilename = signedUpload.signature.uploadFilename

        return upload
      } catch (error) {
        throw new SubmissionUploadException(error, signedUpload.field, signedUpload.file)
      }
    })
  )

  return uploaded.reduce((acc: { [key: string]: any }, upload: any) => {
    const { field, ...data } = upload
    return {
      ...acc,
      [field]: data,
    }
  }, {})
}

const validateBankDetails = async (country: string, bankDetails: any, url: string) => {
  try {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        country: country,
        bankDetails: bankDetails,
      }),
      method: 'POST',
    })

    const resBody = await res.json()

    if (resBody.status === 'OK' && resBody.valid) {
      const validMap: any = {
        Valid: true,
        Invalid: false,
        NotValidated: null,
      }

      return {
        valid: validMap[resBody.valid],
        validated: !!(resBody.valid !== 'NotValidated'),
        reason: resBody?.reason,
      }
    } else {
      return {
        valid: null,
        validated: false,
        reason: resBody?.reason,
      }
    }
  } catch (error) {
    Sentry.captureException(error)
    return {
      valid: null,
      validated: false,
      reason: 'client error',
    }
  }
}

const toOptions = (flatOptions: string[]) => flatOptions.map((item: string) => ({ label: item, value: item }))

export { buildSteps, buildFields, toOptions, uploadItem, handleUploads, validateBankDetails }
